import React from 'react';
import { Button, Modal } from 'react-bootstrap';
import { ReactComponent as DeleteIcon } from '../../images/trash3-fill.svg';
import { ReactComponent as PlayIcon } from '../../images/play-fill.svg';
import { ReactComponent as ViewIcon } from '../../images/eye.svg';
import { ReactComponent as DescriptionIcon } from '../../images/portfolio.svg';
import { ReactComponent as UploadIcon } from '../../images/download.svg';

export interface PromptManageModalProps {
  show: boolean;
  promptText: string;
  descriptionPreview?: string;
  hasVideo: boolean;
  hasDescription: boolean;
  hasDocument: boolean;
  uploadProgressSlot?: React.ReactNode;
  documentUploadProgressSlot?: React.ReactNode;
  onClose: () => void;
  onRecordOrUpload: () => void;
  onDeleteVideo: () => void;
  onViewVideo: () => void;
  onDescription: () => void;
  onUploadDocument: () => void;
  onViewDocument: () => void;
  onDeleteDocument: () => void;
  onChangePrompt: () => void;
  onDone: () => void;
  doneLabel?: string;
}

const PromptManageModal: React.FC<PromptManageModalProps> = ({
  show,
  promptText,
  descriptionPreview,
  hasVideo,
  hasDescription,
  hasDocument,
  uploadProgressSlot,
  documentUploadProgressSlot,
  onClose,
  onRecordOrUpload,
  onDeleteVideo,
  onViewVideo,
  onDescription,
  onUploadDocument,
  onViewDocument,
  onDeleteDocument,
  onChangePrompt,
  onDone,
  doneLabel = 'Save',
}) => {
  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="lg"
      dialogClassName="prompt-manage-modal__dialog"
      className="prompt-manage-modal"
    >
      <Modal.Header closeButton className="prompt-manage-modal__header">
        <Modal.Title className="prompt-manage-modal__title">Manage prompt response</Modal.Title>
      </Modal.Header>
      <Modal.Body className="prompt-manage-modal__body">
        <p className="prompt-manage-modal__prompt">{promptText}</p>

        <section className="prompt-manage-section">
          <h6 className="prompt-manage-section__label">Video response</h6>
          <div className="prompt-manage-actions">
            {!hasVideo ? (
              <button
                type="button"
                className="prompt-manage-action prompt-manage-action--primary"
                onClick={onRecordOrUpload}
              >
                <PlayIcon width={18} height={18} aria-hidden />
                Record or upload video
              </button>
            ) : (
              <>
                <button type="button" className="prompt-manage-action" onClick={onViewVideo}>
                  <ViewIcon width={18} height={18} aria-hidden />
                  View video
                </button>
                <button type="button" className="prompt-manage-action" onClick={onRecordOrUpload}>
                  <PlayIcon width={18} height={18} aria-hidden />
                  Replace video
                </button>
                <button
                  type="button"
                  className="prompt-manage-action prompt-manage-action--danger"
                  onClick={onDeleteVideo}
                >
                  <DeleteIcon width={18} height={18} aria-hidden />
                  Delete video
                </button>
              </>
            )}
          </div>
          {uploadProgressSlot}
        </section>

        <section className="prompt-manage-section">
          <h6 className="prompt-manage-section__label">Description</h6>
          {hasDescription && descriptionPreview ? (
            <p className="prompt-manage-description-preview">{descriptionPreview}</p>
          ) : (
            <p className="prompt-manage-description-hint">
              Add a short note that gives context to your video response. Optional, but helpful for
              recruiters.
            </p>
          )}
          <div className="prompt-manage-actions">
            <button type="button" className="prompt-manage-action" onClick={onDescription}>
              <DescriptionIcon width={18} height={18} aria-hidden />
              {hasDescription ? 'Edit description' : 'Add description'}
            </button>
          </div>
        </section>

        <section className="prompt-manage-section">
          <h6 className="prompt-manage-section__label">Supporting document</h6>
          <p className="prompt-manage-description-hint">
            Share a portfolio, case study, or credential to strengthen your response. Optional.
          </p>
          <div className="prompt-manage-actions">
            {!hasDocument ? (
              <button type="button" className="prompt-manage-action" onClick={onUploadDocument}>
                <UploadIcon width={18} height={18} aria-hidden />
                Upload document
              </button>
            ) : (
              <>
                <button type="button" className="prompt-manage-action" onClick={onViewDocument}>
                  <UploadIcon width={18} height={18} aria-hidden />
                  View document
                </button>
                <button
                  type="button"
                  className="prompt-manage-action prompt-manage-action--danger"
                  onClick={onDeleteDocument}
                >
                  <DeleteIcon width={18} height={18} aria-hidden />
                  Remove document
                </button>
              </>
            )}
          </div>
          {documentUploadProgressSlot}
        </section>

        <section className="prompt-manage-section prompt-manage-section--muted">
          <button
            type="button"
            className="prompt-manage-action prompt-manage-action--ghost"
            onClick={onChangePrompt}
          >
            Change prompt
          </button>
        </section>
      </Modal.Body>
      <Modal.Footer className="prompt-manage-modal__footer">
        <Button variant="outline-secondary" className="prompt-manage-footer-btn" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="success"
          className="prompt-manage-footer-btn prompt-manage-footer-btn--primary"
          onClick={onDone}
        >
          {doneLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PromptManageModal;
